import { Link } from "react-router-dom"
import { Button } from "./ui/button"

export default function AboutUsPage() {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="w-full py-8 md:py-16 lg:py-24 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container px-4 md:px-6">
          <div className="grid gap-6 lg:grid-cols-2 lg:gap-12 items-center">
            <div className="flex flex-col justify-center space-y-4">
              <div className="space-y-2">
                <h1 className="text-3xl font-serif font-bold tracking-tighter sm:text-5xl xl:text-6xl/none">
                  À propos d'Elille Academy
                </h1>
                <p className="max-w-[600px] text-gray-500 md:text-xl dark:text-gray-400">
                  Découvrez notre histoire, notre mission et notre engagement pour une éducation inclusive et transformatrice au Maroc.
                </p>
              </div>
            </div>
            <div className="mx-auto lg:ml-auto">
              <img
                src="/about-hero.png?height=550&width=550"
                alt="À propos d'Elille Academy"
                width={550}
                height={550}
                className="rounded-xl object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/Home.png?height=550&width=550"
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="w-full py-12 md:py-24 lg:py-32">
        <div className="container px-4 md:px-6">
          <div className="grid gap-6 lg:grid-cols-2 lg:gap-12 items-center">
            <div className="flex flex-col justify-center space-y-4">
              <div className="space-y-2">
                <h2 className="text-3xl font-serif font-bold tracking-tighter md:text-4xl">Notre histoire</h2>
                <p className="text-gray-500 dark:text-gray-400 text-lg">
                  Elille Academy est née d'une conviction profonde : l'éducation est le pilier fondamental d'une société juste et égalitaire. Fondée par une équipe de passionnés engagés pour les droits humains, notre académie s'est donnée pour mission de démocratiser l'accès aux connaissances sur l'inclusion, la diversité et l'égalité de genre.
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-lg mt-4">
                  Depuis notre création, nous avons développé une approche pédagogique innovante qui privilégie l'apprentissage participatif et interactif, permettant à chaque apprenant de devenir acteur de sa formation et agent de changement dans sa communauté.
                </p>
              </div>
            </div>
            <div>
              <img
                src="/story.png?height=400&width=600"
                alt="Notre histoire"
                width={600}
                height={400}
                className="rounded-xl object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/mission.png?height=400&width=600"
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="w-full py-12 md:py-24 lg:py-32 bg-gray-100 dark:bg-gray-900">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold tracking-tighter md:text-4xl">Nos valeurs fondamentales</h2>
            <p className="max-w-[900px] mx-auto text-gray-500 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed dark:text-gray-400 mt-4">
              Trois piliers guident notre action quotidienne pour construire un Maroc plus inclusif.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Mission */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-[#00088E] rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-serif font-bold mb-3">Notre Mission</h3>
              <p className="text-gray-500 dark:text-gray-400">
                Démocratiser l'accès à une éducation de qualité sur les droits humains, l'inclusion et l'égalité de genre pour tous les citoyens marocains.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-serif font-bold mb-3">Notre Vision</h3>
              <p className="text-gray-500 dark:text-gray-400">
                Un Maroc où chaque individu, indépendamment de son origine ou de son genre, peut s'épanouir pleinement dans une société juste et solidaire.
              </p>
            </div>

            {/* Values */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-serif font-bold mb-3">Nos Valeurs</h3>
              <p className="text-gray-500 dark:text-gray-400">
                L'inclusion, le respect de la diversité, l'égalité des chances, la solidarité et l'engagement citoyen guident chacune de nos actions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="w-full py-12 md:py-24 lg:py-32">
        <div className="container px-4 md:px-6">
          <div className="grid gap-6 lg:grid-cols-2 lg:gap-12 items-center">
            <div>
              <img
                src="/approach.png?height=400&width=600"
                alt="Notre approche pédagogique"
                width={600}
                height={400}
                className="rounded-xl object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/mission.png?height=400&width=600"
                }}
              />
            </div>
            <div className="flex flex-col justify-center space-y-4">
              <div className="space-y-2">
                <h2 className="text-3xl font-serif font-bold tracking-tighter md:text-4xl">Notre approche pédagogique</h2>
                <p className="text-gray-500 dark:text-gray-400 text-lg">
                  Chez Elille Academy, nous croyons en une pédagogie active et collaborative qui place l'apprenant au cœur du processus éducatif. Nos formations sont conçues pour favoriser l'échange, la réflexion critique et l'application pratique des connaissances.
                </p>
                <div className="space-y-3 mt-6">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#00088E] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-500 dark:text-gray-400">
                      <strong>Apprentissage participatif :</strong> Chaque formation encourage la participation active et les échanges entre apprenants.
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#00088E] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-500 dark:text-gray-400">
                      <strong>Contenu accessible :</strong> Nos ressources sont adaptées à tous les niveaux et disponibles en français et en arabe.
                    </p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-[#00088E] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-500 dark:text-gray-400">
                      <strong>Impact concret :</strong> Nos formations visent à développer des compétences directement applicables dans la vie professionnelle et sociale.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact & Statistics */}
      <section className="w-full py-12 md:py-24 lg:py-32 bg-[#EFF5FF] dark:bg-gray-900">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold tracking-tighter md:text-4xl">Notre impact</h2>
            <p className="max-w-[900px] mx-auto text-gray-500 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed dark:text-gray-400 mt-4">
              Depuis notre création, nous avons touché de nombreuses vies et contribué à construire une société plus inclusive.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-serif font-bold text-[#00088E] mb-2">500+</div>
              <p className="text-gray-500 dark:text-gray-400">Apprenants formés</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-serif font-bold text-[#00088E] mb-2">25+</div>
              <p className="text-gray-500 dark:text-gray-400">Formations disponibles</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-serif font-bold text-[#00088E] mb-2">15+</div>
              <p className="text-gray-500 dark:text-gray-400">Villes couvertes</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-serif font-bold text-[#00088E] mb-2">95%</div>
              <p className="text-gray-500 dark:text-gray-400">Taux de satisfaction</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="w-full py-12 md:py-24 lg:py-32">
        <div className="container px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold tracking-tighter md:text-4xl">Notre équipe</h2>
            <p className="max-w-[900px] mx-auto text-gray-500 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed dark:text-gray-400 mt-4">
              Une équipe passionnée et engagée, unie par la conviction que l'éducation peut transformer notre société.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Team Member 1 */}
            <div className="text-center">
              <div className="relative w-32 h-32 mx-auto mb-4">
                <img
                  src="/team1.png?height=128&width=128"
                  alt="Membre de l'équipe"
                  width={128}
                  height={128}
                  className="rounded-full object-cover w-full h-full"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "/avatar.png?height=128&width=128"
                  }}
                />
              </div>
              <h3 className="text-xl font-serif font-bold mb-2">Équipe Pédagogique</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-2">Experts en droits humains</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Nos formateurs sont des professionnels expérimentés dans les domaines des droits humains et de l'inclusion sociale.
              </p>
            </div>

            {/* Team Member 2 */}
            <div className="text-center">
              <div className="relative w-32 h-32 mx-auto mb-4">
                <img
                  src="/team2.png?height=128&width=128"
                  alt="Membre de l'équipe"
                  width={128}
                  height={128}
                  className="rounded-full object-cover w-full h-full"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "/avatar.png?height=128&width=128"
                  }}
                />
              </div>
              <h3 className="text-xl font-serif font-bold mb-2">Équipe Technique</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-2">Développement digital</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Notre équipe technique développe des solutions innovantes pour rendre l'éducation accessible à tous.
              </p>
            </div>

            {/* Team Member 3 */}
            <div className="text-center">
              <div className="relative w-32 h-32 mx-auto mb-4">
                <img
                  src="/team3.png?height=128&width=128"
                  alt="Membre de l'équipe"
                  width={128}
                  height={128}
                  className="rounded-full object-cover w-full h-full"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "/avatar.png?height=128&width=128"
                  }}
                />
              </div>
              <h3 className="text-xl font-serif font-bold mb-2">Équipe Communication</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-2">Outreach & Sensibilisation</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Spécialisés dans la communication sociale, ils amplifient notre impact et touchent de nouveaux publics.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="w-full py-12 md:py-24 lg:py-32 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container px-4 md:px-6 text-center">
          <div className="mx-auto max-w-3xl space-y-4">
            <h2 className="text-3xl font-serif font-bold tracking-tighter md:text-4xl">Rejoignez notre mission</h2>
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              Ensemble, construisons un Maroc plus juste, plus égalitaire et plus solidaire. Découvrez nos formations et devenez acteur du changement.
            </p>
            <div className="flex flex-col gap-2 min-[400px]:flex-row justify-center mt-8">
              <Link to="/courses" className="px-8 py-2 bg-[#00088E] hover:bg-blue-700 text-white rounded-[3px] text-center">
                Découvrir nos formations
              </Link>
              <Link to="/contact" className="px-8 py-2 border border-[#00088E] text-[#00088E] hover:bg-blue-50 rounded-[3px] text-center">
                Nous contacter
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="w-full py-12 md:py-24 lg:py-32 bg-[#EFF5FF] dark:bg-gray-900">
        <div className="container px-4 md:px-6 text-center">
          <div className="mx-auto max-w-3xl space-y-4">
            <h2 className="text-3xl font-serif font-bold tracking-tighter md:text-4xl">Suivez-nous</h2>
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              Restez connectés avec Elille Academy et suivez nos actualités, événements et nouveautés.
            </p>
            <div className="flex justify-center space-x-4 mt-6">
              <a href="https://www.facebook.com/elillecollective.maroc/?locale=fr_FR" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="icon" className="rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5"
                  >
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                  </svg>
                  <span className="sr-only">Facebook</span>
                </Button>
              </a>
              <a href="https://www.instagram.com/elille.academy/" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="icon" className="rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5"
                  >
                    <rect width="20" height="20" x="2" y="2" rx="5" ry="5"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"></line>
                  </svg>
                  <span className="sr-only">Instagram</span>
                </Button>
              </a>
              <a href="https://www.linkedin.com/company/elille-project/about/?viewAsMember=true" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="icon" className="rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5"
                  >
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                    <rect width="4" height="12" x="2" y="9"></rect>
                    <circle cx="4" cy="4" r="2"></circle>
                  </svg>
                  <span className="sr-only">LinkedIn</span>
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
